import React, { useEffect } from 'react';
import {
  Route, Routes, useNavigate, useLocation,
} from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  locale, addLocale,
} from 'primereact/api';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import PageNotFound from './pages/PageNotFound';
import Partners from './pages/Partners';
import ContactUs from './pages/ContactUs';
import Addresses from './pages/Addresses';
import Laboratory from './pages/Laboratory';
import DiagnosticCenter from './pages/DiagnosticCenter';
import Certificates from './pages/Certificates';
import Career from './pages/Career';
import CheckOut from './pages/CheckOut';
import { getHeaderData } from './store/actions/headerData';
import { getTranslation } from './store/actions/translation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import UserAccount from './pages/user/UserAccount';
import { getPoints } from './store/actions/points';
import { expiredAction } from './store/actions/expiredAction';
import Doctor from './pages/Doctor';
import Service from './pages/Service';
import Faq from './pages/Faq';
import { calendarLocales } from './helpers/primeLocales';
import ThankYou from './pages/ThankYou';
import toggleMenuModal from './store/actions/menuTogle';
import toggleRegisterForm from './store/actions/registerFormToggle';
import callToggle from './store/actions/callToggle';
import HowToTakeTest from './pages/HowToTakeTest';
import ComprehensiveStudies from './pages/ComprehensiveStudies';
import giftToggle from './store/actions/giftToggle';
import { setHeaderCache } from './store/reducers/headerData';
import { setTranslationCache } from './store/reducers/translation';

function ScrollToTop() {
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    localStorage.removeItem('city');
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    dispatch(toggleMenuModal(false));
    const checkUrl = () => {
      const { pathname } = location;
      const searchParams = new URLSearchParams(location.search);
      const isResetPassword = pathname.includes('reset-password');
      const hasToken = searchParams.has('token');
      return isResetPassword && hasToken;
    };

    if (checkUrl()) {
      dispatch(toggleRegisterForm({ visible: true, type: 'reset' }));
    }

    const getPointsFetch = async () => {
      const { payload } = await dispatch(getPoints());
      if (payload?.message === 'Token is Expired') {
        dispatch(expiredAction(true));
      }
    };

    getPointsFetch();
  }, [location]);
  return null;
}

function App() {
  const lang = Cookies.get('i18next') || 'en';
  const { headerData } = useSelector((state) => state.headerData);
  useEffect(() => {
    locale(lang);
    addLocale(lang, calendarLocales[lang]);
  }, [lang]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // eslint-disable-next-line no-restricted-globals
  const path = location.pathname;
  const params = new URLSearchParams(window.location.search);
  const urlVisible = params.get('home-call');
  const santaVisible = params.get('gift');
  useEffect(() => {
    if (santaVisible === 'true') {
      dispatch(giftToggle(true));
    }
  }, [santaVisible]);
  useEffect(() => {
    if (urlVisible === 'open') {
      dispatch(callToggle({ visible: true, isCall: true, item: null }));
    }
  }, [urlVisible]);
  useEffect(() => {
    if (path === '/') {
      navigate(`/${lang}/`);
    }
  }, []);

  useEffect(() => {
    const CACHE_TIME = 60 * 60 * 1000; // 1 час

    const loadData = async () => {
      const headerCache = localStorage.getItem('headerData');
      const translationCache = localStorage.getItem('translation');
      const cacheTime = localStorage.getItem('cacheTime');
      const isValid = cacheTime && Date.now() - Number(cacheTime) < CACHE_TIME;
      if (isValid && headerCache && translationCache) {
        dispatch(setHeaderCache(JSON.parse(headerCache)));
        dispatch(setTranslationCache(JSON.parse(translationCache)));
        return;
      }
      const [header, translation] = await Promise.all([
        dispatch(getHeaderData()).unwrap(),
        dispatch(getTranslation()).unwrap(),
      ]);
      localStorage.setItem(
        'headerData',
        JSON.stringify(header),
      );
      localStorage.setItem(
        'translation',
        JSON.stringify(translation),
      );
      localStorage.setItem(
        'cacheTime',
        Date.now().toString(),
      );
    };
    loadData();
  }, [dispatch]);
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path={`/${lang}`} element={<Home />} />
        <Route path={`/${lang}/reset-password`} element={<Home />} />
        <Route path={`/${lang}/about-us`} element={<AboutUs />} />
        <Route path={`/${lang}/partners`} element={<Partners />} />
        <Route path={`/${lang}/contact-us`} element={<ContactUs />} />
        <Route path={`/${lang}/addresses`} element={<Addresses />} />
        <Route path={`/${lang}/laboratory`} element={<Laboratory />} />
        <Route path={`/${lang}/diagnostic-center`} element={<DiagnosticCenter />} />
        <Route path={`/${lang}/comprehensive-studies`} element={<ComprehensiveStudies />} />
        <Route path={`/${lang}/certificates`} element={<Certificates />} />
        <Route path={`/${lang}/careers`} element={<Career />} />
        <Route path={`/${lang}/check-out`} element={<CheckOut branches={headerData.address} />} />
        <Route path={`/${lang}/privacy-policy`} element={<PrivacyPolicy />} />
        <Route path={`/${lang}/account`} element={<UserAccount />} />
        <Route path={`/${lang}/account/:page`} element={<UserAccount />} />
        <Route path={`/${lang}/doctor/:id`} element={<Doctor />} />
        <Route path={`/${lang}/section-description/:slug`} element={<Service />} />
        <Route path={`/${lang}/faq`} element={<Faq />} />
        <Route path={`/${lang}/thanks`} element={<ThankYou />} />
        <Route path={`/${lang}/how-to-take-a-test`} element={<HowToTakeTest />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
}

export default App;
